"use client";

import { useEffect, useMemo, useRef, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { Menu, Search } from 'lucide-react';
import TechnologyNavigation from './TechnologyNavigation';
import TechnologyMobileNavigation from './TechnologyMobileNavigation';
import { resolveTechnologyNavigation } from '../navigation';

export default function TechnologyHeader({ siteSettings = {}, articles = [], categories = [], pages = [] }) {
  const router = useRouter();
  const searchRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const siteName = siteSettings?.seo?.siteTitle || siteSettings?.organization?.name || 'SURGEDOME';
  const logo = siteSettings?.organization?.logo || null;
  const sticky = siteSettings?.navigation?.options?.stickyHeader !== false;
  const navigationItems = useMemo(() => resolveTechnologyNavigation(siteSettings, categories, pages), [siteSettings, categories, pages]);
  const results = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return [];
    return articles.filter(article => String(article?.title || '').toLowerCase().includes(normalized) || String(article?.category || '').toLowerCase().includes(normalized)).slice(0, 4);
  }, [articles, query]);


  useEffect(() => {
    const closeSearch = event => {
      if (searchRef.current && !searchRef.current.contains(event.target)) setSearchOpen(false);
    };
    document.addEventListener('mousedown', closeSearch);
    return () => document.removeEventListener('mousedown', closeSearch);
  }, []);

  const submitSearch = event => {
    if (event.key === 'Enter' && query.trim()) {
      setSearchOpen(false);
      router.push(`/search?q=${encodeURIComponent(query.trim())}`);
    }
  };


  return (
    <>
      <header className={`${sticky ? 'sticky top-0' : 'relative'} z-50 w-full border-b border-[#e5e5e5] bg-white text-black`}>
        <div className="mx-auto flex min-h-[76px] max-w-[var(--container-width,1200px)] items-center gap-5 px-4 md:px-6">
          <button aria-label="Open menu" onClick={() => setMenuOpen(true)} className="rounded-full p-2 transition-colors hover:bg-[#f1f1f1] hover:text-[#929191] xl:hidden">
            <Menu className="h-6 w-6" />
          </button>
          <Link href="/" className="flex min-w-0 shrink-0 items-center">
            {logo ? <img src={logo} alt={siteName} className="max-h-11 max-w-[180px] object-contain object-left" /> : <span className="truncate text-2xl font-black tracking-tight">{siteName}</span>}
          </Link>
          <TechnologyNavigation items={navigationItems} />
          <div className="ml-auto flex shrink-0 items-center gap-3">
            <div ref={searchRef} className="relative hidden md:block">
              <Search aria-hidden="true" className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#929191]" />
              <input aria-label="Search articles" value={query} onChange={event => { setQuery(event.target.value); setSearchOpen(true); }} onFocus={() => query && setSearchOpen(true)} onKeyDown={submitSearch} placeholder="Search" className="w-36 rounded-full border border-[#d1d1d1] bg-white py-2 pl-9 pr-3 text-sm text-black outline-none transition-colors placeholder:text-[#929191] focus:border-[#929191] lg:w-44" />
              {searchOpen && query.trim() && (
                <div className="absolute right-0 top-full mt-2 w-72 overflow-hidden border border-[#e5e5e5] bg-white shadow-xl">
                  {results.length > 0 ? results.map(article => (
                    <Link key={article.id} href={`/${article.slug}`} onClick={() => setSearchOpen(false)} className="flex gap-3 border-b border-[#eeeeee] p-3 last:border-0 hover:bg-[#f1f1f1]">
                      {article.image && <img src={article.image} alt="" className="h-10 w-10 object-cover" />}
                      <span className="line-clamp-2 text-xs font-bold leading-snug text-black">{article.title}</span>
                    </Link>
                  )) : <div className="p-4 text-center text-xs text-[#6b6b6b]">No results found</div>}
                </div>
              )}
            </div>

          </div>
        </div>
      </header>
      <TechnologyMobileNavigation open={menuOpen} onClose={() => setMenuOpen(false)} items={navigationItems} logo={logo} siteName={siteName} />
    </>
  );
}
